import { Icon } from "@iconify/react";
import type { RecordEffect, RecordOrigin, ReviewTriggerType } from "@/features/patient/records/api/dataPipelineApi";

// Patient-facing wording for each Module 2 review trigger.
export const ISSUE_LABELS: Record<ReviewTriggerType, string> = {
  VENDOR_DISAGREEMENT: "Your records disagree",
  MISSING_INFORMATION: "Some details are missing",
  LOW_CONFIDENCE_EXTRACTION: "We couldn't read this clearly",
  UNRECOGNIZED_NAME: "We couldn't recognize this medicine",
  RXNORM_REVIEW_BAND: "We need to confirm this medicine",
};

const SOURCE_LABELS: Record<string, string> = {
  epic: "Epic",
  athena: "athenahealth",
  athenahealth: "athenahealth",
  ccda: "C-CDA file",
  pdf: "PDF upload",
  image: "Image upload",
  csv: "CSV file",
};

export function sourceLabel(vendor: string | undefined): string {
  if (!vendor) return "Unknown source";
  const key = vendor.toLowerCase();
  return SOURCE_LABELS[key] ?? vendor.charAt(0).toUpperCase() + vendor.slice(1);
}

/** Where a record came from — shown on each side of a conflict card. */
export function SourceBadge({ vendor }: { vendor: string | undefined }) {
  return (
    <span
      data-testid="source-badge"
      className="inline-flex items-center gap-1 rounded-full border border-[#E5E7EB] bg-white px-2 py-0.5 text-[10px] font-bold text-[#4B5563]"
    >
      <Icon icon="lucide:database" className="h-3 w-3" />
      {sourceLabel(vendor)}
    </span>
  );
}

export function UnderReviewBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#FEF6DC] px-2 py-0.5 text-[10px] font-bold text-[#946200]">
      <Icon icon="lucide:hourglass" className="h-3 w-3" />
      Under review
    </span>
  );
}

/** NEEDS_MORE_INFO items: same amber pill as "Under review", different wording. */
export function PhysicianQuestionBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#FEF6DC] px-2 py-0.5 text-[10px] font-bold text-[#946200]">
      <Icon icon="lucide:message-circle-question" className="h-3 w-3" />
      Physician has a question
    </span>
  );
}

const ORIGIN_TAGS: Partial<Record<RecordOrigin, { label: string; icon: string; className: string }>> = {
  PATIENT_REPORTED: { label: "Reported by you", icon: "lucide:user-round", className: "bg-[#EEF2F7] text-[#0A2947]" },
  PHYSICIAN_VERIFIED: { label: "Verified by physician", icon: "lucide:badge-check", className: "bg-[#ECFDF3] text-[#027A48]" },
};

// Module 5: a physician's decision can say more than "verified". KEPT keeps the
// plain "Verified by physician" origin tag; DISMISSED records are never listed.
const EFFECT_TAGS: Partial<Record<RecordEffect, { label: string; icon: string; className: string }>> = {
  CORRECTED: { label: "Corrected by physician", icon: "lucide:pencil-line", className: "bg-[#ECFDF3] text-[#027A48]" },
  DISCONTINUED: { label: "Discontinued by physician", icon: "lucide:circle-stop", className: "bg-[#F3F4F6] text-[#4B5563]" },
};

/**
 * SYSTEM_RESOLVED (the default) and null render nothing, so an ordinary record
 * looks exactly as it did before Module 2. A physician outcome (Module 5) takes
 * precedence over the origin tag when it has its own wording.
 */
export function OriginTag({ origin, effect }: { origin: RecordOrigin | null | undefined; effect?: RecordEffect | null }) {
  const tag = (effect ? EFFECT_TAGS[effect] : undefined) ?? (origin ? ORIGIN_TAGS[origin] : undefined);
  if (!tag) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${tag.className}`}>
      <Icon icon={tag.icon} className="h-3 w-3" />
      {tag.label}
    </span>
  );
}
